import { ClipboardListIcon, PlusIcon } from "lucide-react"
import { toast } from "sonner"
import { format } from "date-fns"
import { useNavigate } from "react-router-dom"
import { AppHeader } from "~/components/AppHeader"
import { EntityIdentity } from "~/components/EntityIdentity"
import {
	EmptyState,
	ErrorState,
	LoadingState
} from "~/components/screens/StateScreen"
import { Button } from "~/components/ui/button"
import { FormCsvExport } from "~/components/forms/FormCsvExport"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	TableRowLink
} from "~/components/ui/table"
import { useTeam } from "~/hooks/useTeam"
import { usePermission } from "~/hooks/usePermission"
import { TeamPermissions } from "~/lib/permissions"
import { api } from "~/trpc/react"

export default function FormsPage() {
	const { team, isLoading, orgSlug } = useTeam()
	const { hasTeamPermission } = usePermission()
	const forms = api.forms.list.useQuery(
		{ teamId: team?.id ?? "" },
		{ enabled: !!team?.enableForms }
	)
	const utils = api.useUtils()
	const navigate = useNavigate()
	const createForm = api.forms.save.useMutation({
		onSuccess: async (saved) => {
			await utils.forms.list.invalidate({ teamId: team?.id ?? "" })
			navigate(`/${orgSlug}/${team?.slug}/forms/${saved.id}`)
		},
		onError: (error) =>
			toast.error(error.message || "Could not create the form")
	})
	const canEdit = hasTeamPermission(TeamPermissions.EDIT_FORMS)
	if (isLoading) return <LoadingState />
	if (!team?.enableForms) return <EmptyState title="Forms are disabled" />
	if (forms.isLoading) return <LoadingState />
	if (forms.error)
		return (
			<ErrorState
				title="Unable to load forms"
				description={forms.error.message}
			/>
		)
	if (!team) return <EmptyState title="Team not found" />
	const base = `/${orgSlug}/${team.slug}/forms`
	return (
		<main className="flex h-full min-h-0 flex-col">
			<AppHeader
				contentClassName="flex items-center gap-2"
				actions={
					<>
						{hasTeamPermission(TeamPermissions.VIEW_FORM_SUBMISSIONS) && (
							<FormCsvExport teamId={team.id} />
						)}
						{canEdit ? (
							<Button
								pending={createForm.isPending}
								onClick={() =>
									createForm.mutate({
										teamId: team.id,
										name: "Untitled form",
										description: ""
									})
								}
							>
								<PlusIcon className="size-4" /> New form
							</Button>
						) : null}
					</>
				}
			>
				<EntityIdentity kind="team" name={team.name} image={team.logo} />
				<span className="text-text-sub">/</span>
				<span className="font-medium">Forms</span>
			</AppHeader>
			<div className="min-h-0 flex-1 overflow-auto px-4 pb-8 sm:px-6">
				{forms.data?.length ? (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Form</TableHead>
								<TableHead>Questions</TableHead>
								<TableHead>Submissions</TableHead>
								<TableHead>Updated</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{forms.data.map((form) => (
								<TableRow key={form.id} linked>
									<TableCell>
										<TableRowLink to={`${base}/${form.id}`}>
											<ClipboardListIcon className="size-4 shrink-0 text-icons" />
											{form.name}
										</TableRowLink>
									</TableCell>
									<TableCell>{form.questions}</TableCell>
									<TableCell>{form.submissions}</TableCell>
									<TableCell>{format(form.updatedAt, "MMM d, yyyy")}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				) : (
					<EmptyState
						scope="inline"
						title="No forms yet"
						description="Create a form to collect structured answers from members or a share link."
					/>
				)}
			</div>
		</main>
	)
}
